import React, { useState } from "react";
import posthog from "posthog-js";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { Delivery, DeliveryStatusEnum } from "@/lib/types/delivery.types";
import { DeliveryCard } from "./delivery-card.component";
import { DeliveryDetailDialog } from "./delivery-detail-dialog.component";
import { useTeamProfilesContext } from "@/dentlab/src/context/single-entity-contexts/TeamProfilesContext";
import { getProfileDisplayName } from "@/lib/utils/client-utils";

export const DeliveriesTab: React.FC = () => {
	const {
		deliveries,
		deliveriesLoading,
		updateDeliveryStatus,
		updateDelivery,
		deleteDelivery,
		fetchDeliveries,
		job,
	} = useJobStore((state) => ({
		deliveries: state.deliveries,
		deliveriesLoading: state.deliveriesLoading,
		updateDeliveryStatus: state.updateDeliveryStatus,
		updateDelivery: state.updateDelivery,
		deleteDelivery: state.deleteDelivery,
		fetchDeliveries: state.fetchDeliveries,
		job: state.job,
	}));

	const {
		teamProfiles: { teamProfilesLookup },
	} = useTeamProfilesContext();
	const [selectedDeliveryId, setSelectedDeliveryId] = useState<number | null>(
		null
	);
	const selectedDelivery =
		selectedDeliveryId !== null
			? deliveries[selectedDeliveryId] ?? null
			: null;

	const getCourierName = (delivery: Delivery | null) => {
		if (!delivery) return undefined;
		if (delivery.courier_type === "person" && delivery.profile_id) {
			const profile = teamProfilesLookup[delivery.profile_id];
			return profile
				? getProfileDisplayName(profile) || "Unbekannt"
				: "Unbekannt";
		}
		return delivery.courier_company_name || "Unbekannt";
	};

	const sortedDeliveries = Object.values(deliveries).sort(
		(a, b) =>
			new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
	);

	const handleStatusChange = async (
		deliveryId: number,
		status: DeliveryStatusEnum
	) => {
		await updateDeliveryStatus(deliveryId, status);
	};

	const handleDelete = async (deliveryId: number) => {
		await deleteDelivery(deliveryId);
	};

	if (!job?.id) return null;

	return (
		<div className="p-2 space-y-2" data-testid="deliveries-tab">
			{deliveriesLoading ? (
				<div className="text-xs text-gray-500 text-center py-4">
					Laden...
				</div>
			) : sortedDeliveries.length === 0 ? (
				<div className="text-center py-6">
					<p className="text-xs text-gray-400">Keine Lieferungen</p>
				</div>
			) : (
				<div className="space-y-2">
					{sortedDeliveries.map((delivery) => (
						<DeliveryCard
							key={delivery.id}
							delivery={delivery}
							onStatusChange={handleStatusChange}
							onDelete={handleDelete}
							onClick={(d) => {
								setSelectedDeliveryId(d.id);
								posthog.capture("delivery_detail_opened", {
									source: "job_page",
								});
							}}
						/>
					))}
				</div>
			)}
			<DeliveryDetailDialog
				delivery={selectedDelivery}
				open={!!selectedDelivery}
				onOpenChange={(open) => {
					if (!open) setSelectedDeliveryId(null);
				}}
				onStatusChange={handleStatusChange}
				onUpdate={(id, fields) => updateDelivery(id, fields)}
				onFilesChanged={() => {
					if (job?.id) fetchDeliveries(job.id);
				}}
				courierName={getCourierName(selectedDelivery)}
			/>
		</div>
	);
};
